export type ParsedUserAgent = {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'unknown';
};

export type StoredDeviceInfo = ParsedUserAgent & { userAgent: string };

const SESSION_TOKEN_KEY = 'blazepos_session_token';
const SESSION_ID_KEY = 'blazepos_session_id';
const SESSION_DEVICE_KEY = 'blazepos_session_device';

export function parseUserAgent(userAgent: string): ParsedUserAgent {
  const result: ParsedUserAgent = {
    browser: 'Unknown',
    browserVersion: '',
    os: 'Unknown',
    osVersion: '',
    deviceType: 'unknown',
  };

  // Detect browser
  if (userAgent.includes('Firefox/')) {
    result.browser = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+(\.\d+)?)/);
    result.browserVersion = match?.[1] || '';
  } else if (userAgent.includes('Edg/')) {
    result.browser = 'Edge';
    const match = userAgent.match(/Edg\/(\d+(\.\d+)?)/);
    result.browserVersion = match?.[1] || '';
  } else if (userAgent.includes('Chrome/')) {
    result.browser = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+(\.\d+)?)/);
    result.browserVersion = match?.[1] || '';
  } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
    result.browser = 'Safari';
    const match = userAgent.match(/Version\/(\d+(\.\d+)?)/);
    result.browserVersion = match?.[1] || '';
  }

  // Detect OS
  if (userAgent.includes('Windows NT')) {
    result.os = 'Windows';
    if (userAgent.includes('Windows NT 10.0')) result.osVersion = '10/11';
    else if (userAgent.includes('Windows NT 6.3')) result.osVersion = '8.1';
    else if (userAgent.includes('Windows NT 6.2')) result.osVersion = '8';
    else if (userAgent.includes('Windows NT 6.1')) result.osVersion = '7';
    result.deviceType = 'desktop';
  } else if (userAgent.includes('Mac OS X')) {
    result.os = 'macOS';
    const match = userAgent.match(/Mac OS X (\d+[._]\d+([._]\d+)?)/);
    result.osVersion = match?.[1]?.replace(/_/g, '.') || '';
    result.deviceType = 'desktop';
  } else if (userAgent.includes('iPhone')) {
    result.os = 'iOS';
    const match = userAgent.match(/iPhone OS (\d+_\d+)/);
    result.osVersion = match?.[1]?.replace(/_/g, '.') || '';
    result.deviceType = 'mobile';
  } else if (userAgent.includes('iPad')) {
    result.os = 'iPadOS';
    const match = userAgent.match(/CPU OS (\d+_\d+)/);
    result.osVersion = match?.[1]?.replace(/_/g, '.') || '';
    result.deviceType = 'tablet';
  } else if (userAgent.includes('Android')) {
    result.os = 'Android';
    const match = userAgent.match(/Android (\d+(\.\d+)?)/);
    result.osVersion = match?.[1] || '';
    result.deviceType = userAgent.includes('Mobile') ? 'mobile' : 'tablet';
  } else if (userAgent.includes('Linux')) {
    result.os = 'Linux';
    result.deviceType = 'desktop';
  }

  return result;
}

function generateSessionToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function getOrCreateSessionToken(): string {
  let token = localStorage.getItem(SESSION_TOKEN_KEY);
  if (!token) {
    token = generateSessionToken();
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  }
  return token;
}

export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

export function setSessionId(sessionId: string | null): void {
  if (!sessionId) {
    localStorage.removeItem(SESSION_ID_KEY);
    return;
  }
  localStorage.setItem(SESSION_ID_KEY, sessionId);
}

export function getSessionId(): string | null {
  return localStorage.getItem(SESSION_ID_KEY);
}

export function setStoredDeviceInfo(info: StoredDeviceInfo): void {
  localStorage.setItem(SESSION_DEVICE_KEY, JSON.stringify(info));
}

export function getStoredDeviceInfo(): StoredDeviceInfo | null {
  const raw = localStorage.getItem(SESSION_DEVICE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredDeviceInfo;
  } catch {
    return null;
  }
}

export function clearSessionStorage(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_ID_KEY);
  localStorage.removeItem(SESSION_DEVICE_KEY);
}
